import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Product, Category, ProductFilters } from '../types';
import { productService } from '../services/productService';
import ProductCard from '../components/ProductCard';
import ResponsiveGrid from '../components/common/ResponsiveGrid';

// Multi-select category dropdown component
interface CategoryDropdownProps {
    categories: Category[];
    selectedCategories: string[];
    onCategoryChange: (categoryId: string) => void;
    onClearAll: () => void;
}

const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
    categories,
    selectedCategories,
    onCategoryChange,
    onClearAll
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getSelectedCategoriesText = () => {
        if (selectedCategories.length === 0) {
            return 'Toutes les catégories';
        }
        if (selectedCategories.length === 1) {
            const category = categories.find(c => c.id.toString() === selectedCategories[0]);
            return category?.name || 'Catégorie sélectionnée';
        }
        return `${selectedCategories.length} catégories sélectionnées`;
    };

    return (
        <div ref={dropdownRef} className="category-dropdown">
            <label className="block text-sm font-medium text-eldai-blue mb-1">
                Catégories
            </label>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="category-dropdown-button"
            >
                <span className="category-dropdown-text">{getSelectedCategoriesText()}</span>
                <svg
                    className={`category-dropdown-icon w-5 h-5 ${isOpen ? 'open' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isOpen && (
                <div className="category-dropdown-menu">
                    {/* Header with clear all */}
                    {selectedCategories.length > 0 && (
                        <div className="category-dropdown-header">
                            <button
                                onClick={() => {
                                    onClearAll();
                                    setIsOpen(false);
                                }}
                                className="category-dropdown-clear"
                            >
                                Tout effacer ({selectedCategories.length})
                            </button>
                        </div>
                    )}

                    {/* Category options */}
                    <div className="category-dropdown-options">
                        {categories.map(category => (
                            <label
                                key={category.id}
                                className="category-option"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedCategories.includes(category.id.toString())}
                                    onChange={() => onCategoryChange(category.id.toString())}
                                    className="category-option-checkbox"
                                />
                                <span className="category-option-label">
                                    {category.name}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Price input component with dropdown styling
interface PriceInputProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
}

const PriceInput: React.FC<PriceInputProps> = ({
    label,
    value,
    onChange,
    placeholder
}) => {
    return (
        <div className="category-dropdown">
            <label className="block text-sm font-medium text-eldai-blue mb-1">
                {label}
            </label>
            <input
                type="number"
                min="0"
                step="0.01"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="category-dropdown-button price-input"
                placeholder={placeholder}
            />
        </div>
    );
};

// Sort dropdown component
interface SortDropdownProps {
    value: string;
    onChange: (value: string) => void;
}

const SortDropdown: React.FC<SortDropdownProps> = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const sortOptions = [
        { value: 'created_at-DESC', label: 'Plus récents' },
        { value: 'created_at-ASC', label: 'Plus anciens' },
        { value: 'unit_price-ASC', label: 'Prix croissant' },
        { value: 'unit_price-DESC', label: 'Prix décroissant' },
        { value: 'designation-ASC', label: 'Nom A-Z' },
        { value: 'designation-DESC', label: 'Nom Z-A' }
    ];

    const getSelectedText = () => {
        const option = sortOptions.find(opt => opt.value === value);
        return option?.label || 'Sélectionner le tri';
    };

    return (
        <div ref={dropdownRef} className="category-dropdown">
            <label className="block text-sm font-medium text-eldai-blue mb-1">
                Trier par
            </label>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="category-dropdown-button"
            >
                <span className="category-dropdown-text">{getSelectedText()}</span>
                <svg
                    className={`category-dropdown-icon w-5 h-5 ${isOpen ? 'open' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isOpen && (
                <div className="category-dropdown-menu">
                    <div className="category-dropdown-options">
                        {sortOptions.map(option => (
                            <button
                                key={option.value}
                                className="category-option w-full"
                                onClick={() => {
                                    onChange(option.value);
                                    setIsOpen(false);
                                }}
                            >
                                <span className="category-option-label">
                                    {option.label}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const CatalogPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    // Filter states
    const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
        // Handle both old 'category' parameter and new 'categories' parameter for backward compatibility
        const categoriesParam = searchParams.get('categories');
        const categoryParam = searchParams.get('category');

        if (categoriesParam) {
            return categoriesParam.split(',');
        } else if (categoryParam) {
            return [categoryParam];
        }
        return [];
    });
    const [minPrice, setMinPrice] = useState(searchParams.get('minPrice') || '');
    const [maxPrice, setMaxPrice] = useState(searchParams.get('maxPrice') || '');
    const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'created_at');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(
        (searchParams.get('sortOrder') as 'ASC' | 'DESC') || 'DESC'
    );

    // Helper function to generate pagination pages
    const generatePaginationPages = (currentPage: number, totalPages: number): (number | string)[] => {
        const pages: (number | string)[] = [];
        const maxVisiblePages = 5;
        
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }
        
        const half = Math.floor(maxVisiblePages / 2);
        
        if (currentPage <= half + 1) {
            return [1, 2, 3, 4, 'ellipsis-end', totalPages];
        }
        
        if (currentPage >= totalPages - half) {
            return [1, 'ellipsis-start', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        }
        
        return [1, 'ellipsis-start', currentPage - 1, currentPage, currentPage + 1, 'ellipsis-end', totalPages];
    };

    // Load data
    useEffect(() => {
        loadData();
        loadCategories();
    }, [currentPage]); // Only reload when page changes

    // Separate effect for URL parameter updates
    useEffect(() => {
        const urlCategoriesParam = searchParams.get('categories');
        const urlCategoryParam = searchParams.get('category');
        const urlMinPrice = searchParams.get('minPrice') || '';
        const urlMaxPrice = searchParams.get('maxPrice') || '';
        const urlSortBy = searchParams.get('sortBy') || 'created_at';
        const urlSortOrder = (searchParams.get('sortOrder') as 'ASC' | 'DESC') || 'DESC';

        // Update state from URL if they differ
        if (urlMinPrice !== minPrice) setMinPrice(urlMinPrice);
        if (urlMaxPrice !== maxPrice) setMaxPrice(urlMaxPrice);
        if (urlSortBy !== sortBy) setSortBy(urlSortBy);
        if (urlSortOrder !== sortOrder) setSortOrder(urlSortOrder);

        // Handle categories from URL
        let urlCategories: string[] = [];
        if (urlCategoriesParam) {
            urlCategories = urlCategoriesParam.split(',');
        } else if (urlCategoryParam) {
            urlCategories = [urlCategoryParam];
        }

        if (JSON.stringify(urlCategories) !== JSON.stringify(selectedCategories)) {
            setSelectedCategories(urlCategories);
        }

        // Load data with URL parameters
        loadData();
    }, [searchParams]); // Run when URL changes

    const loadData = async () => {
        try {
            setLoading(true);

            // Use URL parameters for the actual API call, allowing manual filter application
            const urlSearchTerm = searchParams.get('search') || '';
            const urlCategoriesParam = searchParams.get('categories');
            const urlCategoryParam = searchParams.get('category');
            const urlMinPrice = searchParams.get('minPrice') || '';
            const urlMaxPrice = searchParams.get('maxPrice') || '';
            const urlSortBy = searchParams.get('sortBy') || 'created_at';
            const urlSortOrder = (searchParams.get('sortOrder') as 'ASC' | 'DESC') || 'DESC';

            // Handle categories from URL
            let urlCategories: string[] = [];
            if (urlCategoriesParam) {
                urlCategories = urlCategoriesParam.split(',');
            } else if (urlCategoryParam) {
                urlCategories = [urlCategoryParam];
            }

            const filters: ProductFilters = {
                page: currentPage,
                limit: 12,
                search: urlSearchTerm || undefined,
                categories: urlCategories.length > 0 ? urlCategories.join(',') : undefined,
                minPrice: urlMinPrice ? parseFloat(urlMinPrice) : undefined,
                maxPrice: urlMaxPrice ? parseFloat(urlMaxPrice) : undefined,
                sortBy: urlSortBy,
                sortOrder: urlSortOrder
            };

            const response = await productService.getProducts(filters, true); // Include unavailable products
            setProducts(response.products);
            setTotalPages(response.pagination.totalPages);
            setTotalItems(response.pagination.totalItems);
        } catch (error) {
            console.error('Erreur lors du chargement des produits:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await productService.getCategories();
            setCategories(response.categories);
        } catch (error) {
            console.error('Erreur lors du chargement des catégories:', error);
        }
    };

    // Update URL params when filters change
    const updateUrlParams = (newParams: Record<string, string>) => {
        const params = new URLSearchParams(searchParams);
        Object.entries(newParams).forEach(([key, value]) => {
            if (value) {
                params.set(key, value);
            } else {
                params.delete(key);
            }
        });
        setSearchParams(params);
    };



    const handleFilterChange = (filterType: string, value: string) => {
        // Don't auto-apply filters, just update state
        switch (filterType) {
            case 'minPrice':
                setMinPrice(value);
                break;
            case 'maxPrice':
                setMaxPrice(value);
                break;
            case 'sortBy': {
                const [newSortBy, newSortOrder] = value.split('-');
                setSortBy(newSortBy);
                setSortOrder(newSortOrder as 'ASC' | 'DESC');
                break;
            }
        }
    };

    const applyFilters = () => {
        setCurrentPage(1);
        const categoriesParam = selectedCategories.length > 0 ? selectedCategories.join(',') : '';
        updateUrlParams({
            categories: categoriesParam,
            minPrice,
            maxPrice,
            sortBy,
            sortOrder,
            page: '1'
        });
    };

    const clearAllFilters = () => {
        setSelectedCategories([]);
        setMinPrice('');
        setMaxPrice('');
        setSortBy('created_at');
        setSortOrder('DESC');
        setCurrentPage(1);
        updateUrlParams({
            categories: '',
            minPrice: '',
            maxPrice: '',
            sortBy: 'created_at',
            sortOrder: 'DESC',
            page: '1'
        });
    };

    const handleCategoryChange = (categoryId: string) => {
        let newSelectedCategories: string[];

        if (selectedCategories.includes(categoryId)) {
            // Remove category if already selected
            newSelectedCategories = selectedCategories.filter(id => id !== categoryId);
        } else {
            // Add category if not selected
            newSelectedCategories = [...selectedCategories, categoryId];
        }

        setSelectedCategories(newSelectedCategories);
        // Don't auto-apply filters, let user click the filter button
    };

    const handleAddToCart = (product: Product) => {
        // Cart functionality is handled by ProductCard component
    };

    if (loading && products.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-white">
                <div className="text-center">
                    <div className="spinner mb-4"></div>
                    <div className="text-gray-600 text-lg font-medium">Chargement du catalogue...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-10">
                    <h1 className="heading-2 mb-4">
                        Catalogue des produits
                    </h1>
                    <p className="text-lead max-w-2xl">
                        Découvrez notre sélection de produits casher de qualité premium, soigneusement sélectionnés pour vous
                    </p>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    {/* Filters Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Category Filter Dropdown */}
                        <CategoryDropdown
                            categories={categories}
                            selectedCategories={selectedCategories}
                            onCategoryChange={handleCategoryChange}
                            onClearAll={() => setSelectedCategories([])}
                        />

                        {/* Price Range */}
                        <PriceInput
                            label="Prix minimum (€)"
                            value={minPrice}
                            onChange={(value) => handleFilterChange('minPrice', value)}
                            placeholder="0.00"
                        />

                        <PriceInput
                            label="Prix maximum (€)"
                            value={maxPrice}
                            onChange={(value) => handleFilterChange('maxPrice', value)}
                            placeholder="999.99"
                        />

                        {/* Sort Dropdown */}
                        <SortDropdown
                            value={`${sortBy}-${sortOrder}`}
                            onChange={(value) => handleFilterChange('sortBy', value)}
                        />
                    </div>

                    {/* Filter Buttons */}
                    <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                            onClick={applyFilters}
                            className="filter-button filter-button-primary"
                        >
                            🔍 Appliquer les filtres
                        </button>
                        <button
                            onClick={clearAllFilters}
                            className="filter-button filter-button-secondary"
                        >
                            🗑️ Effacer tous les filtres
                        </button>
                    </div>
                </div>

                {/* Results */}
                <div className="mb-6">
                    <p className="text-eldai-gray">
                        {totalItems} produit{totalItems !== 1 ? 's' : ''} trouvé{totalItems !== 1 ? 's' : ''}
                    </p>
                </div>

                {/* Products Grid */}
                {products.length > 0 ? (
                    <ResponsiveGrid>
                        {products.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                onAddToCart={handleAddToCart}
                            />
                        ))}
                    </ResponsiveGrid>
                ) : (
                    <div className="text-center py-12">
                        <div className="text-6xl mb-4">📦</div>
                        <h3 className="text-xl font-semibold text-eldai-blue mb-2">
                            Aucun produit trouvé
                        </h3>
                        <p className="text-eldai-gray">
                            Essayez de modifier vos critères de recherche
                        </p>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex justify-center items-center space-x-2">
                        <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-4 py-2 border border-eldai-gray rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-eldai-blue hover:text-white transition-colors"
                        >
                            Précédent
                        </button>

                        {generatePaginationPages(currentPage, totalPages).map((page) => {
                            if (typeof page === 'string') {
                                return (
                                    <span key={page} className="px-2 py-2 text-eldai-gray">
                                        ...
                                    </span>
                                );
                            }
                            
                            return (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-4 py-2 border rounded-lg transition-colors ${currentPage === page
                                        ? 'bg-eldai-blue text-white border-eldai-blue'
                                        : 'border-eldai-gray hover:bg-eldai-blue hover:text-white'
                                        }`}
                                >
                                    {page}
                                </button>
                            );
                        })}

                        <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 border border-eldai-gray rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-eldai-blue hover:text-white transition-colors"
                        >
                            Suivant
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CatalogPage;
